import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../../services/tokenService';

const API = '/api';

function buildDocUrls(souscriptionId) {
  return {
    pack1: `${API}/souscriptions/${souscriptionId}/download-pack1`,
    pack2: `${API}/souscriptions/${souscriptionId}/download-pack2`,
    devis: `${API}/souscriptions/${souscriptionId}/download-devis`,
  };
}

function buildDocsObject(urls) {
  return {
    pack1: {
      nom: 'Document RCDS - Attestation',
      description: 'Attestation Garanties solidaire + Note juridique RCDS',
      pages: 7,
      url: urls.pack1
    },
    pack2: {
      nom: 'Document RCDS - Conditions',
      description: 'Conditions Generales + Conditions Particulieres + Attestation',
      pages: 12,
      url: urls.pack2
    },
    devis_ess: {
      nom: 'Devis ESS - Simulation',
      description: 'Recapitulatif des tarifs et activites selectionnees',
      pages: 1,
      url: urls.devis
    }
  };
}

export default function useDocuments(souscriptionCreee, etape) {
  const [documentsGeneres, setDocumentsGeneres] = useState(null);
  const [documentPdfUrl, setDocumentPdfUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [pdfViewerLoading, setPdfViewerLoading] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [documentScrolled, setDocumentScrolled] = useState(false);
  const [documentsLus, setDocumentsLus] = useState({
    proposition: false,
    conditions_particulieres: false,
    conditions_generales: false
  });

  const [generationProgress, setGenerationProgress] = useState({
    isGenerating: false,
    currentStep: 0,
    totalSteps: 4,
    currentLabel: '',
    percentage: 0
  });

  const documentsGenerationTriggered = useRef(false);

  // Generate documents for a given souscription ID
  const genererDocumentsAvecId = async (souscriptionId) => {
    if (!souscriptionId) {
      toast.error('ID de souscription manquant');
      return;
    }

    const progressSteps = [
      { label: 'Preparation des donnees...', percentage: 10 },
      { label: 'Generation du Pack 1 (Attestation + Note juridique)...', percentage: 35 },
      { label: 'Generation du Pack 2 (Conditions Generales + Particulieres)...', percentage: 65 },
      { label: 'Generation du Devis ESS...', percentage: 85 },
      { label: 'Finalisation des documents...', percentage: 100 }
    ];

    setGenerationProgress({
      isGenerating: true,
      currentStep: 0,
      totalSteps: progressSteps.length,
      currentLabel: progressSteps[0].label,
      percentage: progressSteps[0].percentage
    });

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      stepIndex++;
      if (stepIndex < progressSteps.length - 1) {
        setGenerationProgress(prev => ({
          ...prev,
          currentStep: stepIndex,
          currentLabel: progressSteps[stepIndex].label,
          percentage: progressSteps[stepIndex].percentage
        }));
      }
    }, 2500);

    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/souscriptions/${souscriptionId}/generate-document`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      clearInterval(progressInterval);
      setGenerationProgress(prev => ({
        ...prev,
        currentStep: progressSteps.length - 1,
        currentLabel: progressSteps[progressSteps.length - 1].label,
        percentage: 100
      }));

      const urls = buildDocUrls(souscriptionId);
      setDocumentPdfUrl(urls.pack1);

      if (response.data.success) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setDocumentsGeneres(buildDocsObject(urls));
        toast.success('Documents generes avec succes');
      } else {
        setDocumentsGeneres(buildDocsObject(urls));
        toast.success('Documents prets au telechargement');
      }
    } catch {
      clearInterval(progressInterval);
      toast.error('Erreur lors de la generation - Documents de secours disponibles');
      const urls = buildDocUrls(souscriptionId);
      setDocumentPdfUrl(urls.pack1);
      setDocumentsGeneres(buildDocsObject(urls));
    } finally {
      setGenerationProgress({
        isGenerating: false, currentStep: 0, totalSteps: 4, currentLabel: '', percentage: 0
      });
    }
  };

  // Wrapper using current souscriptionCreee
  const genererDocuments = async () => {
    if (!souscriptionCreee?.id) {
      toast.error('Souscription non creee');
      return;
    }
    await genererDocumentsAvecId(souscriptionCreee.id);
  };

  // Initialize docs when entering step 4
  const initDocsForSouscription = (souscriptionId) => {
    const urls = buildDocUrls(souscriptionId);
    setDocumentPdfUrl(urls.pack1);
    setDocumentsGeneres(buildDocsObject(urls));
  };

  // Auto-generate documents at step 4
  useEffect(() => {
    const generateDocsOnStep4 = async () => {
      if (etape === 4 && souscriptionCreee?.id) {
        if (!documentsGenerationTriggered.current) {
          documentsGenerationTriggered.current = true;
          // Utiliser genererDocumentsAvecId qui affiche la progress bar
          // et ne montre les boutons qu'APRES la generation complete
          await genererDocumentsAvecId(souscriptionCreee.id);
        } else if (!documentsGeneres) {
          // Si déjà généré mais state perdu (navigation retour), restaurer les URLs
          initDocsForSouscription(souscriptionCreee.id);
        }
      }
    };
    generateDocsOnStep4();
  }, [etape, souscriptionCreee?.id]);

  // Reset generation trigger on souscription change
  useEffect(() => {
    if (souscriptionCreee?.id) {
      documentsGenerationTriggered.current = false;
    }
  }, [souscriptionCreee?.id]);

  const handlePdfScroll = (e) => {
    const element = e.target;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    if (scrollPercentage > 0.9) setDocumentScrolled(true);
  };

  return {
    documentsGeneres, setDocumentsGeneres,
    documentPdfUrl, setDocumentPdfUrl,
    showPdfViewer, setShowPdfViewer,
    pdfViewerUrl, setPdfViewerUrl,
    pdfViewerTitle, setPdfViewerTitle,
    pdfViewerLoading, setPdfViewerLoading,
    downloadingDoc, setDownloadingDoc,
    documentScrolled, setDocumentScrolled,
    documentsLus, setDocumentsLus,
    generationProgress,
    genererDocumentsAvecId, genererDocuments,
    handlePdfScroll, initDocsForSouscription,
  };
}
